import { digger } from '@cbre/digger';
import { v4 as uuidV4 } from 'uuid';
import { DataObject } from '@shared/models/data-object.model';
import { Column } from '@miq/catalog';
import { CompanyContactsRoleAddressesHelper } from '@miq/domain';

export const KEYS = ['company_id', 'contact_id', 'role_desc', 'location.location_id', 'location_id'];

// Fields that only belong to a contact record - a company-only row (contact_id: null) must never
// carry these, even when it's built alongside a contact in the same "Add Company" action (MIQ-36890).
export const CONTACT_IDENTITY_FIELDS = [
  'first_name',
  'last_name',
  'contact_display_name',
  'familiar_name',
  'email',
  'full_phone_number',
];

export function stripContactIdentityFields<T extends Record<string, any>>(entry: T): T {
  const cleaned = { ...entry };
  CONTACT_IDENTITY_FIELDS.forEach(field => delete cleaned[field]);
  return cleaned;
}

export interface MixedEntityConfigDecomposer {
  parentIdExtractor: string | ((x: Record<string, any>) => string);
  childrenIdExtractor: string | ((x: Record<string, any>) => string);
  parentOverrideData?: Record<string, any>;
}

const keyExtractor = (ctx, extractor) => {
  return typeof extractor === 'function' ? extractor(ctx) : ctx[extractor];
};

/**
 *
 * @param config data to build parent, children relationships
 * @param elements  array of objects to be grouped
 * sample :
 * config = {
 *   parentIdExtractor: ctx => `${ctx['company_id']}.${ctx['role_desc']}.${ctx['location_id']}`,
 *   childrenIdExtractor: 'contact_id',
 * };
 */
export const groupCompaniesContacts = (elements: Record<string, any>[], config: MixedEntityConfigDecomposer = null) => {
  if (!config) {
    config = {
      parentIdExtractor: (ctx: any) =>
        `${ctx['company_id']}.${ctx['role_desc']}.${ctx['location']?.location_id ?? ctx.location_id}`,
      parentOverrideData: { contact_id: null },
      childrenIdExtractor: 'contact_id',
    };
  }

  const parentElements = {};
  CompanyContactsRoleAddressesHelper.SortCompaniesByDisplayOrder(elements || []).forEach(
    (element: Record<string, any>, index) => {
      const parentKey = keyExtractor(element, config.parentIdExtractor);
      // Create company if it doesn't  exists
      if (!parentElements[parentKey]) {
        // adding index to be used in return statement
        // so we return objects as they were created
        parentElements[parentKey] = {
          ...element,
          index: element.display_order ?? index,
          children: [],
          isContainer: true,
          prev_display_order: element.display_order,
          _contact_id: element.contact_id,
          ...config.parentOverrideData,
        };
      }
      const childrenKey = keyExtractor(element, config.childrenIdExtractor);
      // if object is also a valid contact, then append to the element
      if (!!childrenKey && parentElements[parentKey]) {
        // initialized as empty by default
        const nextContact = { ...element, prev_display_order: element.display_order };
        const nextContacts = [...parentElements[parentKey].children, nextContact];
        parentElements[parentKey].children =
          CompanyContactsRoleAddressesHelper.SortContactsByDisplayOrder(nextContacts);
      }
    }
  );

  return (
    Object.values(parentElements)
      // ADDING SORT TO RETURN OBJECTS AS THEY WERE CREATED
      .sort((a: any, b: any) => a.index - b.index)
  );
};

/**
 * this method will return the array of fields which are essential to make company unique
 * @param columns the columns array
 * every clumn will have a parent object and the parent object will have a idField column
 * which has comma separated column.field values
 */
export function getRequiredFields(columns: any[]): string[] {
  const columnWithParentId = (columns || []).find(column => column.parent && column.parent.idField);
  if (columnWithParentId) {
    return columnWithParentId.parent.idField.split(',');
  }
  return [];
}

/**
 * This method will see if there is an existing entity with same values of required fields
 * ex: add company has name and role as required fields. When you try to add a new company,
 * this method checks if there is an existing company with same name and role combination
 * and returns true if there is one
 * @param cfList the existing list of entities
 * @param cfItem the new entity that needs to be added
 * @param requiredFields the required fields
 */
export function isDuplicateComplexField(cfList: any[], cfItem: any, requiredFields: string[]): boolean {
  return findIndexInArray(cfList, cfItem, requiredFields) > -1;
}

export function findIndexInArray(cfList: any[], cfItem: any, requiredFields: string[]): number {
  const companiesCount = cfList ? cfList.length : 0;
  const fieldsCount = requiredFields.length;
  // get the values of required fields from digger and store as a variable to compare against the values in cfList
  const requiredValues = requiredFields.reduce(
    (acc, requiredField) => ({ ...acc, [requiredField]: digger(cfItem, requiredField) }),
    {}
  );
  if (companiesCount > 0) {
    // loop through the existing entities to check if we have a duplicate
    for (let companyIndex = 0; companyIndex < companiesCount; companyIndex++) {
      let matches = 0;
      // loop through the fields to check if how many of them match
      for (let fieldIndex = 0; fieldIndex < fieldsCount; fieldIndex++) {
        if (requiredValues[requiredFields[fieldIndex]] === digger(cfList[companyIndex], requiredFields[fieldIndex])) {
          matches++;
        }
      }
      // if all the reuired fields match, then return true
      if (matches === fieldsCount) {
        return companyIndex;
      }
    }
  }
  return -1;
}

export function getContactCatalog(contactsColumns: Column[]): Column {
  let contactCatalog: Column;

  // Contact ID is a required field and must have parent.delete
  contactCatalog = contactsColumns.find(
    column =>
      column.field === 'company_contact_role_addresses/contact_id' &&
      column.parent != null &&
      column.parent.delete != null
  );

  if (!contactCatalog) {
    contactCatalog = contactsColumns.find(column => column.parent != null && column.parent.delete != null);
  }

  /*
     Contact Section may not have Contact ID as well as parent.delete.
     It is required (any column) while creating a new Lease or a Sale
  */
  return contactCatalog ? contactCatalog : contactsColumns[0];
}

export function setIdToCompanyContactsGrouped(companyContactsGrouped: Array<DataObject>): Array<DataObject> {
  return companyContactsGrouped.map(companyData => {
    if (companyData.children) {
      companyData.children = companyData.children.map(contactData => ({
        ...contactData,
        id: buildTemporalIdWithProperties(contactData, KEYS),
      }));
    }
    return {
      ...companyData,
      id: buildTemporalIdWithProperties(companyData, KEYS),
    };
  });
}

function buildTemporalIdWithProperties(entry: DataObject, properties: Array<string>): string {
  // Security: use uuidv4 to prevent predictable IDs.
  const uuidHex = uuidV4().replace(/-/g, '').slice(0, 8);
  const randNum = (parseInt(uuidHex, 16) >>> 0) % 10000;
  const composedId = properties.reduce((acc, cur) => acc + digger(entry, cur), '');
  return `${composedId}${randNum}`;
}
